import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Mail, Shield, Building2, LogOut, Sun, Moon, Compass } from "lucide-react";
import { PageBody, PageHeader } from "@/components/Page";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import api from "@/lib/api";

function Row({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center justify-between border-b border-border/40 pb-2 last:border-0 last:pb-0">
      <span className="flex items-center gap-2 text-muted-foreground"><Icon className="h-4 w-4" /> {label}</span>
      <span className="text-foreground">{value}</span>
    </div>
  );
}

export default function Profile() {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const [org, setOrg] = useState(null);
  const isAdmin = user?.role === "owner" || user?.role === "admin";

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { api.get("/org/status").then((r) => setOrg(r.data)).catch(() => {}); }, []);
  const doLogout = async () => { await logout(); navigate("/"); };

  return (
    <PageBody>
      <PageHeader eyebrow="Profile" title="Your account" subtitle="Your identity, role and plan for this organization." />
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="rounded-xl border border-border/70 bg-card/50 p-6" data-testid="profile-card">
          <div className="flex items-center gap-4">
            <span className="h-14 w-14 rounded-full bg-amber-500/15 text-amber-500 flex items-center justify-center text-xl font-semibold">{(user?.name || "U").slice(0, 1).toUpperCase()}</span>
            <div><div className="font-display text-lg font-semibold">{user?.name}</div><div className="text-sm text-muted-foreground">{user?.email}</div></div>
          </div>
          <div className="mt-6 space-y-3 text-sm">
            <Row icon={Mail} label="Email" value={user?.email} />
            <Row icon={Shield} label="Role" value={<Badge variant="outline" className="capitalize">{user?.role}</Badge>} />
            <Row icon={Building2} label="Plan" value={org?.plan_name || "No active plan"} />
          </div>
        </div>
        <div className="space-y-4">
          <div className="rounded-xl border border-border/70 bg-card/50 p-5 flex items-center justify-between" data-testid="profile-theme">
            <div className="flex items-center gap-2 text-sm">{theme === "dark" ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />} Appearance</div>
            <Button variant="outline" size="sm" className="rounded-md" onClick={toggle} data-testid="profile-theme-toggle">{theme === "dark" ? "Switch to light" : "Switch to dark"}</Button>
          </div>
          {isAdmin && (
            <>
              <button onClick={() => navigate("/app/admin/team")} className="w-full rounded-xl border border-border/70 bg-card/50 p-4 flex items-center justify-between hover:bg-accent/40 transition-colors" data-testid="profile-team-link"><span className="text-sm">Team &amp; seats</span><span className="text-xs text-muted-foreground">Open</span></button>
              <button onClick={() => navigate("/app/admin/billing")} className="w-full rounded-xl border border-border/70 bg-card/50 p-4 flex items-center justify-between hover:bg-accent/40 transition-colors" data-testid="profile-billing-link"><span className="text-sm">Billing &amp; usage</span><span className="text-xs text-muted-foreground">Open</span></button>
            </>
          )}
          <div className="rounded-xl border border-border/70 bg-card/50 p-5 flex items-center justify-between" data-testid="profile-tour">
            <div className="flex items-center gap-2 text-sm"><Compass className="h-4 w-4 text-amber-500" /> Product tour</div>
            <Button variant="outline" size="sm" className="rounded-md" data-testid="profile-replay-tour"
              onClick={async () => { try { await api.post("/me/tour", { status: "reset" }); } catch { /* noop */ } if (user?.id) localStorage.removeItem(`dealsops_tour_shown_${user.id}`); window.dispatchEvent(new CustomEvent("dealsops:replay-tour")); }}>Replay tour</Button>
          </div>
          <Button variant="ghost" className="text-rose-500 w-full justify-start" onClick={doLogout} data-testid="profile-logout"><LogOut className="h-4 w-4 mr-2" /> Sign out</Button>
        </div>
      </div>
    </PageBody>
  );
}
